import { useEffect, useRef } from "react";
import { onChildAdded, orderByChild, query, ref, limitToLast } from "firebase/database";

import { database } from "../firebase/firebaseConfig";
import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

const parsePayloadJson = (value) => {
  if (!value || typeof value !== "string") return {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

function useAdminOrderNotifications({ onNotification }) {
  const seenNotificationIdsRef = useRef(new Set());

  useEffect(() => {
    const markRead = async (notificationIds) => {
      const endpoint = appSettings?.endpoints?.markNotificationsRead;
      if (!endpoint || !Array.isArray(notificationIds) || notificationIds.length === 0) return;

      await fetchWithAuth(`${appSettings.baseUrl}${endpoint}`, {
        method: "POST",
        body: JSON.stringify({ notificationIds }),
      });
    };

    const processNotification = async (notification) => {
      const notificationId = Number(notification?.notificationId ?? notification?.id ?? 0);
      if (!Number.isFinite(notificationId) || notificationId <= 0) {
        return;
      }

      if (seenNotificationIdsRef.current.has(notificationId)) {
        return;
      }

      seenNotificationIdsRef.current.add(notificationId);
      console.info("Notification received by dashboard", notification);
      await onNotification?.(notification);
      await markRead([notificationId]);
    };

    const loadUnreadNotifications = async () => {
      const endpoint = appSettings?.endpoints?.getUnreadNotifications;
      if (!endpoint) return;

      const response = await fetchWithAuth(`${appSettings.baseUrl}${endpoint}`, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to load unread notifications.");
      }

      const items = await response.json();
      for (const item of Array.isArray(items) ? items : []) {
        const payload = parsePayloadJson(item?.payloadJson || item?.PayloadJson);
        await processNotification({
          notificationId: item?.id ?? item?.Id,
          title: item?.title ?? item?.Title,
          message: item?.message ?? item?.Message,
          ...payload,
        });
      }
    };

    void loadUnreadNotifications().catch((error) => {
      console.error("Unread notifications load failed", error);
    });

    const notificationQuery = query(
      ref(database, "notifications/admin-orders"),
      orderByChild("notificationId"),
      limitToLast(1)
    );

    const unsubscribe = onChildAdded(notificationQuery, (snapshot) => {
      const value = snapshot.val();
      void processNotification(value).catch((error) => {
        console.error("Firebase notification processing failed", error);
      });
    });

    return () => {
      unsubscribe();
    };
  }, [onNotification]);
}

export default useAdminOrderNotifications;
